import { useEffect, useState } from "react";
import { SubpageTitle } from "../../../components/atoms/text/titles";
import { useQueryBackend } from "../../../utils/requestHelpers";
import { Nullable } from "../../../common-types";
import { BarChartView, IDataItem } from "../../../components/atoms/charts";
import { getDistinctPropertyValues } from "../../../functions";
import { DateTime } from "luxon";
import { DateRangePickerPopUp } from "../../../components/molecules/datepickers/dateRangePickerPopUp";
import { TagButton } from "../../../components/molecules/datepickers/tagButtons";
import ViewBox from "../../../components/atoms/views/viewBox";
import { colors, IColorMap } from "../../../components/atoms/charts/barChartView";

interface CyclesItem {
  day: string;
  store: Nullable<string>;
  cycles: number;
}

export const SalesPerStore = ({
  nodeId,
  name,
}: {
  nodeId?: string;
  name?: string | undefined;
}) => {
  const queryApi = useQueryBackend("Backoffice");
  const [from, setFrom] = useState<DateTime>(DateTime.now().startOf("month"));
  const [to, setTo] = useState<DateTime>(DateTime.now().endOf("month"));
  const [data, setData] = useState<Array<IDataItem>>([]);
  const [colorMap, setColorMap] = useState<IColorMap>({})
  const today = DateTime.now()
  const step = "month"
  const stepper = { month: 1 }
  const todayRollingSeven = today.minus(stepper);
  const lastMonthStart = today.minus(stepper).startOf("month");
  const lastMOnthEnd = today.minus(stepper).endOf("month");

  const expandColorMap = (allStoresFetched: (string | null | undefined)[]) => {
    setColorMap((prevMap) => {
      const newMap = { ...prevMap };
      let hasChanges = false;
      
      let assignedCount = Object.keys(newMap).length;

      allStoresFetched
        .filter((s): s is string => !!s) 
        .forEach((storeName) => {

          if (!(storeName in newMap)) {
            newMap[storeName] = colors[assignedCount % colors.length];
            assignedCount++;
            hasChanges = true;
          }
        });

      return hasChanges ? newMap : prevMap;
    });
  };

  useEffect(() => {

    if (!from || !to) 
      return

    if (nodeId) {
      queryApi
        .Get<Array<CyclesItem>>(
          `nodes/${nodeId}/stores/daily/between/${from.toFormat(
            "yyyy-MM-dd"
          )}/and/${to.plus({ day: 1 }).toFormat("yyyy-MM-dd")}`
        )
        .Data((d) => {
          const allDays = getDistinctPropertyValues(d, "day");
          const allStores = getDistinctPropertyValues(d, "store")

          const storeTotals: Record<string, number> = {}
          d.forEach((row) => {
            if (row.store) {
              storeTotals[row.store] = (storeTotals[row.store] || 0) + row.cycles;
            }
          })

          allStores.sort((a, b) => {
            if (!a || !b) return 0
            return storeTotals[a] - storeTotals[b]
          });
          expandColorMap(allStores);
          let items = allDays.map((day) => {
            const item: IDataItem = { name: day }
            allStores
              .filter((s) => !!s)
              .forEach((k) => {
                item[k!] =
                  d.find((d) => d.day === day && d.store === k)?.cycles ?? 0
              });
            return item;
          }).map((item) => {
            const total = Object.entries(item)
              .filter(([key, value]) => key !== 'name' && typeof value === 'number')
              .reduce((sum, [, value]) => sum + (value as number), 0);

            return { ...item, Total: total > 0 ? total : undefined };
          });
          setData(items);
        });
    } else {
      queryApi
        .Get<Array<CyclesItem>>(
          `customer/nodes/recipes/all-nodes/stores/daily/between/${from.toFormat(
            "yyyy-MM-dd"
          )}/and/${to.plus({ day: 1 }).toFormat("yyyy-MM-dd")}?viewName=report_sales_view`
        )
        .Data((d) => {
          const allDays = getDistinctPropertyValues(d, "day");
          const allStores = getDistinctPropertyValues(d, "store");

          const storeTotals: Record<string, number> = {};
          d.forEach((row) => {
            if (row.store) {
              storeTotals[row.store] = (storeTotals[row.store] || 0) + (Number(row.cycles) || 0);
            }
          });

          allStores.sort((a, b) => {
            if (!a || !b) return 0
            return storeTotals[a] - storeTotals[b]
          });
          expandColorMap(allStores)
          const items = allDays.map((day) => {
            const item: IDataItem = { name: day }
            allStores
              .filter((s) => !!s)
              .forEach((k) => {
                item[k!] =
                  d.find((d) => d.day === day && d.store === k)?.cycles ?? 0;
              })

            return item;
          }).map((item) => {
            const total = Object.entries(item)
              .filter(([key, value]) => key !== 'name' && typeof value === 'number')
              .reduce((sum, [, value]) => sum + (value as number), 0);

            return { ...item, Total: total > 0 ? total : undefined };
          });
          setData(items);
        });
    }
  }, [nodeId, from, to]);
  const [open, setOpen] = useState<boolean>(false);

  const presets: TagButton[] = [
    {
      name: "This Month",
      id: "this",
      action: () => {
        setFrom(today.startOf(step));
        setTo(today.endOf("month"));
        setOpen(false);
      },
      default: true
    },
    {
      name: "Last Month",
      id: "last",
      action: () => {
        setFrom(lastMonthStart);
        setTo(lastMOnthEnd);
        setOpen(false);
      },
    },
    {
      name: "Rolling month",
      id: "rolling",
      action: () => {
        setFrom(todayRollingSeven);
        setTo(today);
        setOpen(false);
      },
    },
  ];
  return (
    <ViewBox extend={{overflow: "visible"}}>
      <SubpageTitle title={name ?? "Sales per customer"} />
      <ViewBox>
        <DateRangePickerPopUp
          startDate={from}
          endDate={to}
          onStartDatePicked={setFrom}
          onEndDatePicked={setTo}
          presets={presets}
          open={open}
          setOpen={setOpen}
        ></DateRangePickerPopUp>
      </ViewBox>
      <div style={{ width: "100%", height: "400px" }}>
        <BarChartView
          hideLegend
          items={data}
          colorMap={colorMap}
          formatX={(s) => s.substring(s.lastIndexOf("-") + 1)}
          shouldShowGraphLabel
          highlightWeekends
        />
      </div>
    </ViewBox>
  );
};
